import { FeedbackToasts } from "./feedback";
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

import {
  ApiError,
  createApiClient,
  type PlatformDataClient,
  type PlatformSession,
  type PlatformUser,
} from "@taunggyi-plus/api-client";

import {
  getBrowserAuthClient,
  isAuthConfigured,
  readBrowserSession,
  writeBrowserSession,
  type BrowserAuthConfig,
} from "./client";

export interface AuthState {
  readonly client: PlatformDataClient | null;
  readonly session: PlatformSession | null;
  readonly user: PlatformUser | null;
  readonly loading: boolean;
  readonly configured: boolean;
  signIn(identifier: string, password: string): Promise<string | null>;
  signUp(
    identifier: string,
    password: string,
    displayName: string,
    username: string,
  ): Promise<string | null>;
  updateProfile(displayName: string, username: string): Promise<string | null>;
  updateContact(currentPassword: string, contact: string): Promise<string | null>;
  updatePassword(currentPassword: string, newPassword: string): Promise<string | null>;
  requestRecovery(email: string): Promise<string>;
  resetPassword(token: string, password: string): Promise<string>;
  signOut(): Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

function authErrorMessage(error: unknown) {
  const message = error instanceof Error ? error.message : "Authentication failed.";
  if (/failed to fetch|network request failed|load failed/i.test(message)) {
    return "Cannot connect to authentication. Check your internet connection and try again.";
  }
  return message;
}

export function AuthProvider({
  config,
  children,
}: {
  config: Partial<BrowserAuthConfig>;
  children: ReactNode;
}) {
  const apiBaseUrl = config.apiBaseUrl;
  const appId = config.appId;
  const resolved = useMemo(() => {
    const candidate = { apiBaseUrl, appId };
    return isAuthConfigured(candidate) ? getBrowserAuthClient(candidate) : null;
  }, [apiBaseUrl, appId]);
  const [session, setSession] = useState<PlatformSession | null>(() => readBrowserSession(appId));
  const [loading, setLoading] = useState(Boolean(resolved && session));

  useEffect(() => {
    if (!resolved || !session) {
      return;
    }
    let active = true;
    let checking = false;
    const check = async () => {
      if (checking || document.visibilityState === "hidden") return;
      checking = true;
      try {
        if (session.expires_at <= Math.floor(Date.now() / 1000)) {
          if (active) {
            writeBrowserSession(null, appId);
            setSession(null);
          }
          return;
        }
        await resolved.api.me();
        if (appId) {
          const access = await resolved.dataClient.rpc("my_app_access", { target_app: appId });
          if (access.error) throw new Error(access.error.message);
          if (!access.data && active) {
            writeBrowserSession(null, appId);
            setSession(null);
            return;
          }
        }
        if (session.expires_at < Math.floor(Date.now() / 1000) + 60 * 60 * 24 * 14) {
          const response = await resolved.api.renewSession();
          if (active) {
            writeBrowserSession(response.session, appId);
            setSession(response.session);
          }
        } else if (active) writeBrowserSession(session, appId);
      } catch (error) {
        // A temporary network failure must not discard a valid local session.
        if (active && error instanceof ApiError && error.status === 401) {
          writeBrowserSession(null, appId);
          setSession(null);
        }
      } finally {
        checking = false;
        if (active) setLoading(false);
      }
    };
    void check();
    const timer = window.setInterval(() => void check(), 30000);
    const wake = () => {
      void check();
    };
    document.addEventListener("visibilitychange", wake);
    return () => {
      active = false;
      clearInterval(timer);
      document.removeEventListener("visibilitychange", wake);
    };
  }, [resolved, session, appId]);

  const value = useMemo<AuthState>(
    () => ({
      client: resolved?.dataClient ?? null,
      session,
      user: session?.user ?? null,
      loading,
      configured: Boolean(resolved),
      async signIn(identifier, password) {
        if (!resolved) return "Authentication is not configured.";
        try {
          const response = await resolved.api.authenticate(identifier.trim(), password);
          if (appId && apiBaseUrl) {
            const candidate = createApiClient(
              apiBaseUrl,
              async () => response.session.access_token,
            );
            const access = await candidate.dataClient.rpc("my_app_access", { target_app: appId });
            if (access.error) return access.error.message;
            if (!access.data)
              return "This account does not have access to this app. Use your account for this role.";
          }
          writeBrowserSession(response.session, appId);
          setSession(response.session);
          return null;
        } catch (error) {
          return authErrorMessage(error);
        }
      },
      async signUp(identifier, password, displayName, username) {
        if (!resolved) return "Authentication is not configured.";
        try {
          const response = await resolved.api.register({
            identifier: identifier.trim(),
            password,
            displayName: displayName.trim(),
            username: username.trim().toLowerCase(),
          });
          writeBrowserSession(response.session, appId);
          setSession(response.session);
          return null;
        } catch (error) {
          return authErrorMessage(error);
        }
      },
      async updateProfile(displayName, username) {
        if (!resolved) return "Authentication is not configured.";
        try {
          const r = await resolved.dataClient.rpc("update_my_profile", {
            display_name: displayName,
            username,
          });
          if (r.error) return r.error.message;
          const response = await resolved.api.renewSession();
          writeBrowserSession(response.session, appId);
          setSession(response.session);
          return null;
        } catch (error) {
          return authErrorMessage(error);
        }
      },
      async updateContact(currentPassword, contact) {
        if (!resolved) return "Authentication is not configured.";
        try {
          const response = await resolved.api.updateContact({
            currentPassword,
            contact: contact.trim(),
          });
          writeBrowserSession(response.session, appId);
          setSession(response.session);
          return null;
        } catch (error) {
          return authErrorMessage(error);
        }
      },
      async updatePassword(currentPassword, newPassword) {
        if (!resolved) return "Authentication is not configured.";
        try {
          const response = await resolved.api.updatePassword({ currentPassword, newPassword });
          writeBrowserSession(response.session, appId);
          setSession(response.session);
          return null;
        } catch (error) {
          return authErrorMessage(error);
        }
      },
      async requestRecovery(email) {
        if (!resolved) throw new Error("Authentication is not configured.");
        return (await resolved.api.requestRecovery(email.trim(), window.location.origin)).message;
      },
      async resetPassword(token, password) {
        if (!resolved) throw new Error("Authentication is not configured.");
        const result = await resolved.api.resetPassword(token, password);
        writeBrowserSession(null, appId);
        setSession(null);
        return result.message;
      },
      async signOut() {
        writeBrowserSession(null, appId);
        setSession(null);
      },
    }),
    [loading, resolved, session, appId, apiBaseUrl],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
      <FeedbackToasts />
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside AuthProvider");
  return value;
}
